'use client';

import { useMutation } from '@tanstack/react-query';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import mixpanel from 'mixpanel-browser';

import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';

import { APPLICANT_MODE_WORKSPACE_ID } from '@/features/user-settings/types/workspace-types';

import { Tooltip } from '@/shared/components';
import { APP_ENVS } from '@/shared/constants/envs';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { register } from '@/shared/services/auth';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { tabStorage } from '@/shared/utils/tab-storage';

import { getApiErrorDetails } from '@/utils/axios';
import { ILoginOTPFormState, registerOTPSchema } from '@/utils/schema';

const OTP_LENGTH = 6;

interface IRegisterModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	email: string;
	name: string;
	onRegisterSuccess: () => void;
	formId?: string;
	formName?: string;
}

function RegisterModal({
	open,
	onOpenChange,
	email,
	name,
	onRegisterSuccess,
	formId,
	formName,
}: IRegisterModalProps) {
	const [otpFormState, setOtpFormState] = useState<ILoginOTPFormState>({
		otp: '',
		validator: registerOTPSchema,
		error: '',
	});

	// useMutation for login
	const registerMutation = useMutation({
		mutationFn: register,
		onSuccess: (data) => {
			tabStorage.set('selectedWorkspaceId', APPLICANT_MODE_WORKSPACE_ID);
			tabStorage.set('isFreshLogin', true);
			onRegisterSuccess();

			if (data.newUser && data?.email) {
				mixpanel.alias(data?.email);
				mixpanel.identify(data?.email);
			}
		},
		onError: (error) => {
			const { errorMessage, errorStatusCode } = getApiErrorDetails(error);

			if (errorStatusCode === 403) {
				setOtpFormState((prevState) => ({
					...prevState,
					error: 'Too many attempts!',
				}));
			} else {
				setOtpFormState((prevState) => ({
					...prevState,
					error: errorMessage,
				}));
			}
		},
		retry: undefined,
	});

	const isLoading = registerMutation.isPending;

	const modalHeader = 'Enter confirmation code';
	const modalDescription = `To confirm your email, we just sent a 6 digit code to ${email}.`;

	const validateAndCallRegister = (otp: string) => {
		const validationResult = registerOTPSchema.safeParse({ otp });

		if (validationResult.error) {
			const fieldErrors = validationResult.error.flatten().fieldErrors;
			setOtpFormState((prevState) => ({
				...prevState,
				error: fieldErrors.otp?.[0] ?? '',
			}));
			return;
		}

		registerMutation.mutate({
			login_type: 'EMAIL',
			token_id: null,
			otp,
			email: email?.trim(),
			name,
		});
	};

	const handleSubmitRegister = (e: React.FormEvent) => {
		e.preventDefault();
		if (isLoading) return;

		validateAndCallRegister(otpFormState.otp);
	};

	const handleOtpInputChange = (otp: string) => {
		if (isLoading) return;

		if (otp.length === OTP_LENGTH) {
			validateAndCallRegister(otp);
		}

		setOtpFormState((prevState) => ({
			...prevState,
			otp,
			error: '',
		}));
	};

	const handleOtpInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) return;

		if (e.key === 'Enter') {
			e.preventDefault();
			validateAndCallRegister(otpFormState.otp);
		}
	};

	const handleOpenChange = (open: boolean) => {
		if (isLoading) return;
		onOpenChange(open);
	};

	useEffect(() => {
		emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_CODE_MODAL, {
			feature: MIXPANEL_APP_FEATURES.FORM,
			form_id: formId,
			form_name: formName,
			user_email: email,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Dialog
			open={open}
			onOpenChange={handleOpenChange}
		>
			<DialogContent className="subpixel-antialiased sm:w-[360px]">
				<DialogHeader>
					<div className="flex flex-col gap-3">
						<svg
							width="64px"
							height="64px"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z"
								stroke="currentColor"
								strokeWidth="1"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<DialogTitle className="text-2xl">{modalHeader}</DialogTitle>
					</div>
				</DialogHeader>
				<DialogDescription>{modalDescription}</DialogDescription>

				<form
					onSubmit={handleSubmitRegister}
					className="space-y-6"
				>
					<div className="justify-center space-y-2">
						<Label
							htmlFor="otp-input-0"
							className="sr-only"
						>
							OTP Input
						</Label>
						<InputOTP
							maxLength={OTP_LENGTH}
							value={otpFormState.otp}
							onChange={handleOtpInputChange}
							pattern={REGEXP_ONLY_DIGITS}
							onKeyDown={handleOtpInputKeyDown}
							className="w-full"
						>
							<InputOTPGroup className="flex-1 justify-between">
								<InputOTPSlot index={0} />
								<InputOTPSlot index={1} />
								<InputOTPSlot index={2} />
								<InputOTPSlot index={3} />
								<InputOTPSlot index={4} />
								<InputOTPSlot index={5} />
							</InputOTPGroup>
						</InputOTP>
						{otpFormState.error && (
							<p className="text-start text-sm text-red-500">{otpFormState.error}</p>
						)}
					</div>
					<div className="mt-2 flex items-center">
						<Tooltip
							text={`If you haven't received the code, please check your spam folder once. If
										the issue persists, reach out to us at ${APP_ENVS.COMPANY_SUPPORT_EMAIL}`}
						>
							<p className="text-sm">Didn&apos;t receive the code?</p>
						</Tooltip>
					</div>
					<Button
						type="submit"
						className="h-auto w-full py-3 text-base subpixel-antialiased"
						disabled={isLoading}
					>
						{isLoading ? 'Submitting...' : 'Submit'}
					</Button>
				</form>
			</DialogContent>
		</Dialog>
	);
}

export default RegisterModal;
